<template>
  <div>
    <transition-group name="domains">
      <div
        v-for="(domain, index) in visibleDomains"
        :key="domain.uuid"
        class="d-flex"
      >
        <magic-container>
          <dropdowns-countries
            v-model="domain.countries"
            :error-messages="countriesErrors(index)"
            :return-object="true"
            @update:model-value="checkIfChanged(index)"
            @keyup.enter="applyChanges(index)"
          />

          <v-text-field
            v-model="domain.domain"
            inset
            :label="$t('main_domain')"
            :error-messages="domainErrors(index)"
            @update:model-value="checkIfChanged(index)"
            @keyup.enter="applyChanges(index)"
          />
        </magic-container>

        <!-- delete domain button -->
        <v-btn
          v-if="!changedIndexes.has(index) && domain.id !== -1"
          class="ml-6"
          color="primary"
          @click="deleteDomain(domain)"
          :disabled="isLoading"
          icon
          variant="outlined"
          density="comfortable"
        >
          <v-icon class="material-icons-outlined">delete</v-icon>
        </v-btn>

        <!-- cancel adding domain button -->
        <v-btn
          v-if="!changedIndexes.has(index) && domain.id === -1"
          class="ml-6"
          color="primary"
          @click="cancelAddingDomain(index, true)"
          :disabled="isLoading"
          icon
          variant="outlined"
          density="comfortable"
        >
          <v-icon class="material-icons-outlined">close</v-icon>
        </v-btn>

        <!-- cancel edit button -->
        <v-btn
          v-if="changedIndexes.has(index)"
          class="ml-5"
          color="primary"
          @click="cancelEdit(index)"
          :disabled="isLoading"
          icon
          variant="outlined"
          density="comfortable"
        >
          <v-icon class="material-icons-outlined">close</v-icon>
        </v-btn>

        <!-- apply changes button -->
        <!-- || v$.domains[index].$invalid -->
        <v-btn
          v-if="changedIndexes.has(index)"
          class="ml-2"
          color="primary"
          @click="applyChanges(index)"
          :disabled="isLoading"
          icon
          variant="outlined"
          density="comfortable"
        >
          <v-icon class="material-icons-outlined">done</v-icon>
        </v-btn>
      </div>
    </transition-group>

    <div class="d-flex gap-8">
      <v-btn
        v-if="domains.length > 5 + addedDomainsCount"
        @click="toggleExpanded"
        variant="text"
        dense
      >
        <v-icon v-if="!expanded">keyboard_arrow_down</v-icon>
        <v-icon v-else>keyboard_arrow_up</v-icon>
        {{
          expanded ?
          $t('collapse') :
          $t('view_all')
        }}
      </v-btn>

      <v-btn
        :disabled="isAddingDomain"
        color="primary"
        @click="addDomainFields"
        variant="outlined"
        dense
      >
        <v-icon>add</v-icon>
        {{ $t('add_domain') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { required, helpers } from '@vuelidate/validators';
import { isNum, isNonEmptyArr } from '@/utils';
import { isEqual } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { useVuelidate } from '@vuelidate/core'

export default {
  setup () {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      expanded: false,
      isLoading: false,
      domains: [],
      changedIndexes: new Map(),
      appliedIndexes: new Set(),
      addedDomainsCount: 0, // is used to keep visible domains when we add more
      isAddingDomain: false,
      refreshDomainsKey: 0, // a simple primitive to force re-evaluation
    };
  },
  validations() {
    return {
      domains: {
        $each: helpers.forEach({
          domain: {
            required,
          },
          countries: {
            required,
          },
        }),
      },
    }
  },
  computed: {
    visibleDomains() {
      if (this.expanded) {
        return this.domains;
      }
      return this.domains.slice(0, 5 + this.addedDomainsCount);
    },
    originalDomains() {
      // Access the key to make the computed property dependent on it
      // eslint-disable-next-line no-unused-vars
      const refresh = this.refreshDomainsKey;

      const storeArr = this.$store.getters['tracking/advertiserDomains'];

      return storeArr.map((domain) => ({
        ...domain,
        countries: domain.countries.map((country) => {
          return {
            ...country,
            code: country.country,
          };
        }),
      }))
      .reverse(); // reverse to show the last added domain first
    },
    advertiserId() {
      return this.$store.getters['tracking/activeAdvertiser']?.id;
    },
  },
  created() {
    this.getDomains();
  },
  watch: {
    originalDomains: {
      handler() {
        if (this.domains === null || isNonEmptyArr(this.originalDomains) || this.originalDomains.length < this.domains.length) {
          // todo: reset validation
          // this.v$.$reset();
          this.domains = this.originalDomains.map((domain) => {
            return {
              ...domain,
              countries: [...domain.countries],
            };
          });
        }
      },
      deep: true,
      immediate: true,
    },
    advertiserId() {
      this.getDomains();
    }
  },
  methods: {
    // isDeleteField is used to prevent deleting the fields.
    // the animation works only if the field are the same, so we need to keep them in some cases
    cancelAddingDomain(index, isDeleteField = false) {
      if (isDeleteField) {
        this.domains.splice(index, 1);
        this.addedDomainsCount--;
      }
      this.changedIndexes.delete(index);
      this.appliedIndexes.delete(index);
      this.isAddingDomain = false;
    },
    getDomains() {
      if (isNum(this.advertiserId)) {
        this.isLoading = true;
        this.$store.dispatch('tracking/getDomains', this.advertiserId)
          .catch((err) => err)
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    cancelEdit(index) {
      this.changedIndexes.delete(index);
      this.appliedIndexes.delete(index);
      this.v$.$reset();

      // -1 means it's a new domain
      if (this.domains[index].id === -1) {
        this.cancelAddingDomain(index, true);
      } else { // else is for existing domains that has original value
        const uuid = this.domains[index].uuid;
        const originalDomain = this.originalDomains.find((domain) => domain.uuid === uuid);
        this.domains[index].domain = originalDomain.domain;
        this.domains[index].countries = [...originalDomain.countries];
      }
    },
    applyChanges(index) {
      this.appliedIndexes.add(index);
      // Access array item validation state directly by index
      const domainValidator = this.v$.domains.$each.$response.$errors[index];
      if (
        domainValidator.domain.length ||
        domainValidator.countries.length
      ) return;

      this.isLoading = true;

      // if domain has id, it means it's an edit
      if (this.domains[index].id !== -1) {
        this.editDomain(index);
      } else {
        this.addDomain(index);
      }
    },
    editDomain(index) {
      const query = {};
      query.domain = this.removeSchemeAndWWW(this.domains[index].domain);
      query.countries = this.domains[index].countries.map((country) => {
        return {
          id: country.id,
          country: country.code }
      });
      query.uuid = this.domains[index].uuid; // keep the uuid for proper animation

      this.$store.dispatch('tracking/updateDomain', {
        advertiserId: this.advertiserId,
        domainId: this.domains[index].id,
        domain: query,
      }).then(() => {
        this.v$.$reset();
        this.changedIndexes.delete(index);
        // This forces the originalDomains computed property to re-run because
        // its dependency (refreshDomainsKey) has changed
        this.refreshDomainsKey += 1;
      })
      .catch((err) => err)
      .finally(() => {
        this.isLoading = false;
      });
    },
    addDomain(index) {
      const query = {};
      query.domain = this.removeSchemeAndWWW(this.domains[index].domain);
      query.countries = this.domains[index].countries.map((country) => country.code);
      query.uuid = this.domains[index].uuid; // keep the uuid for proper animation

      this.isLoading = true;
      this.$store.dispatch('tracking/createDomain', {
        advertiserId: this.advertiserId,
        domain: query,
      }).then(() => {
        this.v$.$reset();
        this.cancelAddingDomain(index);
      })
      .catch((err) => err)
      .finally(() => {
        this.isLoading = false;
      });
    },
    removeSchemeAndWWW(domain) {
      return domain.replace(/^http(s?):\/\//i, "").replace(/^www[.]/i, "");
    },
    addDomainFields() {
      this.addedDomainsCount++; // increase counter to make new fields visible
      this.isAddingDomain = true;

      const newDomain = {
        id: -1, // -1 is used to identify new domain in the list
        uuid: uuidv4(),
        domain: '',
        countries: [],
      };

      // decides where to insert new fields
      let newDomainFieldsIndex = this.domains.length;
      if (this.expanded) {
        newDomainFieldsIndex = this.domains.length;
      } else {
        if (this.domains.length > 5) {
          newDomainFieldsIndex = 5 + this.addedDomainsCount - 1;
        } else {
          newDomainFieldsIndex = this.domains.length + this.addedDomainsCount;
        }
      }

      this.domains.splice(newDomainFieldsIndex, 0, newDomain);
      this.changedIndexes.set(newDomainFieldsIndex, true);
    },
    checkIfChanged(index) {
      if (
        this.checkIfDomainIsChanged(index) ||
        this.checkIfCountriesAreChanged(index)
      ) {
        this.changedIndexes.set(index, true);
      } else {
        this.changedIndexes.delete(index);
      }
    },
    checkIfDomainIsChanged(index) {
      // if it's a new domain, it's changed
      if (this.domains[index].id === -1) return true;

      // if it's an existing domain, check if it's changed
      return this.domains[index].domain !== this.originalDomains[index].domain;
    },
    checkIfCountriesAreChanged(index) {
      // if it's a new domain, it's changed
      if (this.domains[index].id === -1) return true;

      // if it's an existing domain, check if it's changed
      return !isEqual(this.domains[index].countries, this.originalDomains[index].countries);
    },
    domainErrors(index) {
      if (!this.appliedIndexes.has(index)) return [];
      const errors = [];
      const itemValidation = this.v$.domains.$each.$response.$errors[index].domain;
      if (itemValidation.length === 0) return errors;
      itemValidation[0].$validator === 'required' && errors.push(this.$t('domain_required'));
      return errors;
    },
    countriesErrors(index) {
      if (!this.appliedIndexes.has(index)) return [];
      const errors = [];
      const itemValidation = this.v$.domains.$each.$response.$errors[index].countries;
      if (itemValidation.length === 0) return errors;
      itemValidation[0].$validator === 'required' && errors.push(this.$t('country_is_required'));
      return errors;
    },
    deleteDomain(domain) {
      this.$router.push({
        name: 'tracking-management-advertiser-domain-delete',
        params: {
          id: this.advertiserId,
          domainId: domain.id,
        },
      });
    },
    toggleExpanded() {
      this.addedDomainsCount = 0;
      this.expanded = !this.expanded;
      if (this.isAddingDomain) {
        this.cancelAddingDomain(this.domains.findIndex((domain) => domain.id === -1), true);
      }
    },
  },
};
</script>

<style scoped>
/* animation */
.domains-move {
  transition: transform 0.3s ease;
}
</style>
